import React, { useState, useEffect, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { ArrowLeft, Trophy, Plus, RefreshCw, Users, Clock, Swords, Target, Skull, Gamepad2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getGuestId = () => {
  let guestId = sessionStorage.getItem('metal_lego_guest_id');
  if (!guestId) {
    guestId = 'guest_' + Math.random().toString(36).substr(2, 8);
    sessionStorage.setItem('metal_lego_guest_id', guestId);
  }
  return guestId;
};

const MODE_INFO = {
  high_score: { icon: <Trophy className="w-5 h-5" />, label: 'High Score', color: '#FFD700', desc: 'Highest score wins' },
  time_attack: { icon: <Clock className="w-5 h-5" />, label: 'Time Attack', color: '#5AC8FA', desc: 'Best score in time limit' },
  survival: { icon: <Skull className="w-5 h-5" />, label: 'Survival', color: '#FF3B30', desc: 'Survive the longest' },
};

const TournamentBrowser = ({ onBack, onViewTournament }) => {
  const { publicKey } = useWallet();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: 'Metal Lego Tournament',
    playerName: 'Player',
    bracketSize: 8,
    gameMode: 'high_score',
    timeLimit: 120,
  });

  const playerId = publicKey?.toBase58() || getGuestId();

  const fetchTournaments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tournaments`);
      setTournaments(res.data.tournaments || []);
    } catch (e) {
      console.error('Error fetching tournaments:', e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchTournaments();
    const interval = setInterval(fetchTournaments, 10000);
    return () => clearInterval(interval);
  }, [fetchTournaments]);

  const handleCreate = async () => {
    try {
      const res = await axios.post(`${API}/tournament/create`, {
        name: createForm.name,
        host_id: playerId,
        host_name: createForm.playerName || 'Player',
        bracket_size: createForm.bracketSize,
        game_mode: createForm.gameMode,
        time_limit: createForm.timeLimit,
      });
      toast.success('Tournament created!');
      onViewTournament(res.data.tournament.tournament_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create tournament');
    }
  };

  const handleJoin = async (tournamentId) => {
    try {
      await axios.post(`${API}/tournament/${tournamentId}/join`, {
        player_id: playerId,
        player_name: createForm.playerName || 'Player',
      });
      toast.success('Joined tournament!');
      onViewTournament(tournamentId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to join');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Button onClick={onBack} variant="ghost" className="text-[#A1A1AA] hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 mr-2" /> Back
          </Button>
          <Button onClick={fetchTournaments} className="bg-[#333] hover:bg-[#444]" data-testid="refresh-tournaments">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        <div className="text-center mb-8">
          <h1 className="font-game text-3xl md:text-4xl text-[#FFD700] mb-2 flex items-center justify-center gap-3">
            <Trophy className="w-8 h-8" /> TOURNAMENTS
          </h1>
          <p className="text-[#A1A1AA] text-sm">Compete in single-elimination bracket tournaments</p>
        </div>

        {/* Create Tournament */}
        <div className="glass-card p-6 mb-8">
          {!showCreate ? (
            <Button onClick={() => setShowCreate(true)} className="w-full bg-[#FFD700] text-black hover:bg-[#FFD700]/90 py-4 font-game" data-testid="show-create-tournament">
              <Plus className="w-5 h-5 mr-2" /> CREATE TOURNAMENT
            </Button>
          ) : (
            <div className="space-y-4">
              <h2 className="font-game text-lg text-[#FFD700]">NEW TOURNAMENT</h2>
              <div>
                <label className="block text-[#A1A1AA] text-xs mb-1">Tournament Name</label>
                <input type="text" value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})}
                  className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-[#FFD700]"
                  data-testid="tournament-name-input" />
              </div>
              <div>
                <label className="block text-[#A1A1AA] text-xs mb-1">Your Name</label>
                <input type="text" value={createForm.playerName} onChange={e => setCreateForm({...createForm, playerName: e.target.value})}
                  className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-[#FFD700]"
                  data-testid="tournament-player-name" />
              </div>
              <div>
                <label className="block text-[#A1A1AA] text-xs mb-1">Bracket Size</label>
                <div className="flex gap-2">
                  {[4, 8, 16].map(s => (
                    <Button key={s} onClick={() => setCreateForm({...createForm, bracketSize: s})}
                      className={`flex-1 ${createForm.bracketSize === s ? 'bg-[#FFD700] text-black' : 'bg-[#333]'}`}>
                      {s} Players
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[#A1A1AA] text-xs mb-1">Game Mode</label>
                <div className="flex gap-2">
                  {Object.entries(MODE_INFO).map(([id, info]) => (
                    <Button key={id} onClick={() => setCreateForm({...createForm, gameMode: id})}
                      className={`flex-1 text-xs ${createForm.gameMode === id ? 'text-black' : 'bg-[#333]'}`}
                      style={createForm.gameMode === id ? { backgroundColor: info.color } : {}}>
                      {info.icon} <span className="ml-1">{info.label}</span>
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[#A1A1AA] text-xs mb-1">Time Limit (seconds)</label>
                <div className="flex gap-2">
                  {[60, 120, 180].map(t => (
                    <Button key={t} onClick={() => setCreateForm({...createForm, timeLimit: t})}
                      className={`flex-1 ${createForm.timeLimit === t ? 'bg-[#FFD700] text-black' : 'bg-[#333]'}`}>
                      {t}s
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setShowCreate(false)} className="flex-1 bg-[#333] hover:bg-[#444]">Cancel</Button>
                <Button onClick={handleCreate} className="flex-1 bg-[#FFD700] text-black hover:bg-[#FFD700]/90" data-testid="create-tournament-btn">Create</Button>
              </div>
            </div>
          )}
        </div>

        {/* Tournament List */}
        <div className="glass-card p-6">
          <h2 className="font-game text-lg text-white mb-4 flex items-center gap-2">
            <Swords className="w-5 h-5 text-[#FFD700]" /> ACTIVE TOURNAMENTS
          </h2>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-[#FFD700] border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-[#A1A1AA]">Loading...</p>
            </div>
          ) : tournaments.length === 0 ? (
            <div className="text-center py-12">
              <Trophy className="w-12 h-12 text-[#333] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">No active tournaments</p>
              <p className="text-[#666] text-sm mt-2">Create one to get started!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tournaments.map(t => {
                const mode = MODE_INFO[t.game_mode] || MODE_INFO.high_score;
                const isFull = t.players?.length >= t.bracket_size;
                const isJoined = t.players?.some(p => p.player_id === playerId);
                return (
                  <div key={t.tournament_id} className="flex items-center justify-between p-4 bg-[#1a1a1a] rounded-lg border border-[#333] hover:border-[#FFD700] transition-colors"
                    data-testid={`tournament-${t.tournament_id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${mode.color}20` }}>
                        <span style={{ color: mode.color }}>{mode.icon}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-game text-white">{t.name}</span>
                          <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: `${mode.color}20`, color: mode.color }}>
                            {mode.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-[#A1A1AA]">
                          <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {t.players?.length || 0}/{t.bracket_size}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t.time_limit}s</span>
                          <span className="capitalize text-xs" style={{ color: t.status === 'registration' ? '#14F195' : '#FF9500' }}>{t.status.replace('_', ' ')}</span>
                        </div>
                      </div>
                    </div>
                    {isJoined ? (
                      <Button onClick={() => onViewTournament(t.tournament_id)} className="bg-[#9945FF] hover:bg-[#9945FF]/90">VIEW</Button>
                    ) : t.status === 'registration' && !isFull ? (
                      <Button onClick={() => handleJoin(t.tournament_id)} className="bg-[#FFD700] text-black hover:bg-[#FFD700]/90">JOIN</Button>
                    ) : (
                      <Button onClick={() => onViewTournament(t.tournament_id)} className="bg-[#333] hover:bg-[#444]">VIEW</Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TournamentBrowser;
